import type { Section } from '../auth/useSession'

export type SectionCopy = {
  label: string
  description: string
}

/**
 * The sections of the catalogue, in Spanish, for the roles screen. A `Record`
 * over the router's own `Section` union, so a section added to the API's
 * catalogue stops this file compiling until somebody writes what it opens —
 * the SPA does not depend on `@wasp/models` to read `SECTIONS` itself.
 *
 * The key order is the order the screen lists them in: the planner's screens
 * first, then this application's.
 */
const SECTION_COPY: Record<Section, SectionCopy> = {
  planes: { label: 'Planes', description: 'Ver los planes de producción del día en el planificador.' },
  planificar: { label: 'Planificar', description: 'Generar, editar, restaurar y publicar planes.' },
  pendientes: { label: 'Pendientes', description: 'Las órdenes que quedan por planificar.' },
  tablero: { label: 'Tablero ensamblado', description: 'El tablero de ejecución de cocina, con Play, Pausa y Stop.' },
  mise_en_place: { label: 'Mise en place', description: 'La bolsa de trabajo por días y el reparto del equipo.' },
  racionado_caliente: { label: 'Racionado caliente', description: 'La cola de semis a abatir y su reparto por abatidores.' },
  racionado_frio: { label: 'Racionado frío', description: 'Sacar lo abatido, racionar y termosellar los tuppers.' },
  abatimientos: {
    label: 'Abatimientos',
    description: 'El registro de abatimientos por lote: ver, filtrar, informar y corregir.',
  },
  almacen: { label: 'Almacén', description: 'Pesaje de recetas' },
  compras: { label: 'Compras', description: 'Las faltas del pesaje: seguirlas y marcarlas recibidas.' },
  semanas: { label: 'Semanas', description: 'La planificación de las semanas de MRPeasy: crearlas, ajustarlas, reservar su CO y cerrarlas.' },
  export: { label: 'Exportar', description: 'Exportar planes y fichas de olla en PDF.' },
  configuracion: {
    label: 'Configuración del planificador',
    description: 'Máquinas, reglas de calidad, cocción baja, homologación BM y parámetros.',
  },
  admin: { label: 'Usuarios y roles', description: 'Dar de alta personas y decidir qué ve cada rol.' },
  grupos: { label: 'Grupos y asignaciones', description: 'Grupos de estación y quién trabaja en cada uno.' },
  operaciones: { label: 'Operaciones', description: 'Las operaciones en curso en planta.' },
}

const isSection = (value: string): value is Section => value in SECTION_COPY

/** Every section of the catalogue, in the order the roles screen lists them. */
export const SECTION_CATALOG: Array<Section> = Object.keys(SECTION_COPY).filter(isSection)

export const sectionCopy = (section: Section): SectionCopy => SECTION_COPY[section]

/** A section as stored in a change log, by its label; a value the catalogue no longer has, as it is. */
export const sectionLabel = (value: string): string => (isSection(value) ? SECTION_COPY[value].label : value)
